import React from "react";
import moment from "moment";
import { Table } from "reactstrap";

import ShellCard from "components/ShellCard";

import { fmtBRL, fmtInt, labelSituacao } from "../../ConferenciaFatura/formatters";
import renderEstadoSecao from "./EstadoSecao";

const EYEBROW = "Foto de hoje";
const TITULO = "Aging por situação";
const DESCRICAO =
  "Onde as faturas estão paradas AGORA e há quanto tempo, contando da última transição. As seis situações aparecem sempre. O período do filtro entra só como corte de entrada — o passivo mais antigo fica de fora.";

// As quatro faixas do contrato, com o MESMO tom do card de Backlog da H1
// (estatisticas.js/FAIXAS_AGING): as duas visões de envelhecimento na mesma
// tela não podem pintar "16 a 30 dias" de cores diferentes. `warning` fica de
// fora porque no tema escuro ele é um salmão que quase se confunde com o rosa
// de `danger` em colunas vizinhas; o tom do meio vem de accent (roxo), que
// separa nos dois temas.
const FAIXAS = [
  { key: "0-7", tone: "success", label: "Até 7 d" },
  { key: "8-15", tone: "info", label: "8 a 15 d" },
  { key: "16-30", tone: "accent", label: "16 a 30 d" },
  { key: "30+", tone: "pink", label: "+ de 30 d" },
];

const fmtDias = (v) =>
  v === null || v === undefined
    ? "-"
    : `${Number(v).toLocaleString("pt-BR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      })} d`;

/**
 * Seção da H3 (PO1-3001): aging por situação. Complementa o card de Backlog da
 * H1 (que envelhece pela emissão, sem quebrar por situação) — aqui a idade é
 * contada da ÚLTIMA transição, que é o que diz há quanto tempo a fatura está
 * efetivamente parada naquele estado.
 */
export default function AgingSituacao({ estado }) {
  const bloqueio = renderEstadoSecao(estado, "Nenhuma fatura em aberto no período.");

  if (bloqueio) {
    return (
      <ShellCard eyebrow={EYEBROW} title={TITULO} description={DESCRICAO}>
        {bloqueio}
      </ShellCard>
    );
  }

  const { situacoes, corteEntrada } = estado.data;
  const totalFaturas = situacoes.reduce((s, x) => s + (x.faturas || 0), 0);
  const totalValor = situacoes.reduce((s, x) => s + (x.valorTotal || 0), 0);

  return (
    <ShellCard eyebrow={EYEBROW} title={TITULO} description={DESCRICAO}>
      <div className="lt-aging-resumo">
        <div>
          <span className="lt-aging-resumo__label">Em aberto</span>
          <strong>{fmtInt(totalFaturas)}</strong>
        </div>
        <div>
          <span className="lt-aging-resumo__label">Valor parado</span>
          <strong>{fmtBRL(totalValor)}</strong>
        </div>
        <div>
          <span className="lt-aging-resumo__label">Corte de entrada</span>
          <strong>
            {corteEntrada ? moment(corteEntrada).format("DD/MM/YYYY") : "—"}
          </strong>
        </div>
      </div>

      <div className="lt-table-wrap">
        <Table className="lt-table" responsive>
          <thead>
            <tr>
              <th>Situação</th>
              <th className="lt-num">Faturas</th>
              <th className="lt-num">Valor</th>
              <th className="lt-num">Idade média</th>
              {FAIXAS.map((f) => (
                <th key={f.key} className="lt-num">
                  <span className={`lt-dot lt-dot--${f.tone}`} />
                  {f.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {situacoes.map((s) => (
              <tr key={s.codigo} className={s.faturas ? "" : "is-vazia"}>
                <td>{labelSituacao(s.codigo, s.nome)}</td>
                <td className="lt-num">{fmtInt(s.faturas)}</td>
                <td className="lt-num">{fmtBRL(s.valorTotal)}</td>
                <td className="lt-num">{fmtDias(s.idadeMediaDias)}</td>
                {FAIXAS.map((f) => {
                  const faixa = s.faixas[f.key] || { faturas: 0, valor: 0 };
                  return (
                    <td key={f.key} className="lt-num">
                      <span className="lt-faixa-qtd">{fmtInt(faixa.faturas)}</span>
                      <span className="lt-faixa-valor">{fmtBRL(faixa.valor)}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <p className="lt-footnote">
        A idade é contada da última transição registrada (ou da entrada, quando a
        fatura ainda não teve nenhuma). Faturas Integradas e Canceladas não
        entram: já saíram da esteira.
      </p>
    </ShellCard>
  );
}
